import { useState, useEffect, useRef } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import SuperadminLayout from '../../components/SuperadminLayout';
import SearchSelect from '../../components/SearchSelect';
import { confirm } from '../../components/ConfirmDialog';
import { Upload, Loader2, Package, TriangleAlert, ArchiveRestore, CircleCheck } from 'lucide-react';
import RestoreCenter from './RestoreCenter';

// Superadmin → Baza Restore: backup .zip-i yüklə, hansı tenant olduğunu göstər,
// təsdiqlə və o tenantı bərpa et. Yalnız seçilmiş tenanta təsir edir.
const CHUNK = 500 * 1024;

export default function Restore() {
    const [tenants, setTenants] = useState([]);
    const [uploadId, setUploadId] = useState('');
    const [meta, setMeta]       = useState(null);   // {tenant_id, username, name, ...}
    const [targetId, setTargetId] = useState('');
    const [phase, setPhase]     = useState('idle'); // idle|uploading|inspecting|ready|restoring|done
    const [pct, setPct]         = useState(0);       // yükləmə %-i
    const [rpct, setRpct]       = useState(0);       // bərpa (təxmini) %-i
    const fileRef = useRef();
    const creepRef = useRef(null);

    const startCreep = () => {
        stopCreep(); let v = 0; setRpct(0);
        creepRef.current = setInterval(() => {
            v = Math.min(95, v + Math.max(0.4, (95 - v) * 0.05));
            setRpct(Math.round(v));
        }, 400);
    };
    const stopCreep = () => { if (creepRef.current) { clearInterval(creepRef.current); creepRef.current = null; } };
    useEffect(() => () => stopCreep(), []);

    useEffect(() => {
        api.get('/superadmin/tenants').then(r => setTenants(r.data.data || [])).catch(() => {});
    }, []);

    const reset = () => {
        setUploadId(''); setMeta(null); setTargetId('');
        setPhase('idle'); setPct(0);
        if (fileRef.current) fileRef.current.value = '';
    };

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith('.zip')) { toast.error('.zip faylı seçin.'); return; }
        setMeta(null); setTargetId('');

        // ── Hissə-hissə yüklə (nginx 1MB limitini keçmək üçün) ──
        setPhase('uploading'); setPct(0);
        const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        const total = Math.ceil(f.size / CHUNK);
        try {
            for (let i = 0; i < total; i++) {
                const chunk = f.slice(i * CHUNK, Math.min((i + 1) * CHUNK, f.size));
                const fd = new FormData();
                fd.append('chunk', chunk, `c-${i}`);
                fd.append('uploadId', id);
                fd.append('chunkIndex', String(i));
                fd.append('totalChunks', String(total));
                await api.post('/superadmin/tenant-restore/upload-chunk', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
                });
                setPct(Math.round(((i + 1) / total) * 100));
            }
            setUploadId(id);

            // meta oxu
            setPhase('inspecting');
            const r = await api.post('/superadmin/tenant-restore/inspect', { uploadId: id });
            if (!r.data.ok) throw new Error(r.data.msg);
            setMeta(r.data.meta);
            setTargetId(String(r.data.meta.tenant_id)); // default = mənşə tenant
            setPhase('ready');
        } catch (err) {
            toast.error('Yükləmə/oxuma xətası: ' + (err.response?.data?.msg || err.message));
            reset();
        }
    };

    const doRestore = async () => {
        if (!uploadId || !targetId) return;
        const tName = tenants.find(t => String(t.id) === String(targetId));
        const ok = await confirm({
            title: 'Bərpanı təsdiqlə',
            message: `"${tName?.name || tName?.username || targetId}" tenantının HAZIRKI məlumatı backup ilə əvəzlənəcək. Əvvəlcə hazırkı vəziyyət avtomatik nüsxələnir (Təhlükəsizlik nüsxələri). Şəkillər silinmir, yalnız əlavə olunur. Digər tenantlara toxunulmur. Davam edilsin?`,
            confirmText: 'Bəli, bərpa et',
            danger: true,
        });
        if (!ok) return;

        setPhase('restoring');
        startCreep();
        const t = toast.loading('Bərpa olunur…');
        try {
            const r = await api.post('/superadmin/tenant-restore/commit',
                { uploadId, targetTenantId: Number(targetId) }, { timeout: 600000 });
            if (!r.data.ok) throw new Error(r.data.msg);
            stopCreep(); setRpct(100);
            toast.success(r.data.msg || 'Bərpa tamamlandı', { id: t, duration: 8000 });
            setPhase('done');
        } catch (err) {
            stopCreep();
            toast.error(err.response?.data?.msg || err.message, { id: t });
            setPhase('ready');
        }
    };

    const opts = tenants.map(t => ({ value: t.id, label: `${t.name || t.username} (#${t.id})` }));
    const mismatch = meta && String(targetId) !== String(meta.tenant_id);

    return (
        <SuperadminLayout title="Baza restore" subtitle="Tək tenant .zip faylından, gecə backup-larından tək tenant və ya bütün baza">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Tək tenant · Baza backup-dan endirilmiş .zip</p>
            <div className="max-w-2xl rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                {phase === 'done' ? (
                    <div className="py-6 text-center">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                            <CircleCheck className="h-7 w-7" />
                        </div>
                        <p className="mt-3 text-base font-semibold text-slate-900">Bərpa tamamlandı</p>
                        <button onClick={reset} className="mt-4 rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                            Yenidən başla
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Addım 1 */}
                        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] text-white">1</span>
                            Backup faylını seçin
                        </div>
                        <label className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
                            phase === 'uploading' || phase === 'restoring' ? 'cursor-not-allowed border-slate-200 bg-slate-50' : 'border-slate-300 hover:border-[#0090bb] hover:bg-sky-50/40'
                        }`}>
                            <Upload className="h-6 w-6 text-slate-400" />
                            <span className="mt-2 text-sm font-semibold text-slate-700">.zip faylını seçmək üçün klikləyin</span>
                            <span className="mt-0.5 text-xs text-slate-400">Baza backup bölməsindən endirilmiş fayl</span>
                            <input ref={fileRef} type="file" accept=".zip" onChange={onPick}
                                disabled={phase === 'uploading' || phase === 'restoring'}
                                className="hidden" />
                        </label>

                        {phase === 'uploading' && (
                            <div className="mt-4">
                                <div className="mb-1.5 flex justify-between text-xs font-medium text-slate-500">
                                    <span>Yüklənir…</span><span className="font-mono">{pct}%</span>
                                </div>
                                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                    <div className="h-full rounded-full bg-[#0090bb] transition-all" style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        )}
                        {phase === 'inspecting' && (
                            <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Fayl oxunur…</p>
                        )}

                        {meta && (
                            <div className="mt-6 border-t border-slate-100 pt-6">
                                <div className="flex items-start gap-3 rounded-xl bg-sky-50 p-3.5 text-xs text-sky-900">
                                    <Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" />
                                    <div>
                                        Bu backup: <b>{meta.name || meta.username}</b> (id={meta.tenant_id}, <code className="font-mono">{meta.username}</code>)
                                        <div className="mt-0.5 text-sky-700/80">Tarix: {meta.created_at?.slice(0, 10)}</div>
                                    </div>
                                </div>

                                <div className="mb-3 mt-6 flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] text-white">2</span>
                                    Hədəf restoranı təsdiqləyin
                                </div>
                                <SearchSelect value={targetId} onChange={setTargetId} options={opts}
                                    placeholder="Restoran seçin" />
                                {mismatch && (
                                    <p className="mt-2 flex gap-2 rounded-xl bg-amber-50 p-3 text-xs font-medium text-amber-800">
                                        <TriangleAlert className="h-4 w-4 flex-shrink-0 text-amber-500" />
                                        <span>Fərqli tenant seçdiniz. Bərpa yalnız backup-un öz tenantına (<b>{meta.username}</b>, id={meta.tenant_id}) mümkündür. Həmin tenantı seçin.</span>
                                    </p>
                                )}

                                <button onClick={doRestore} disabled={phase === 'restoring' || mismatch}
                                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-rose-700 disabled:opacity-50">
                                    {phase === 'restoring' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArchiveRestore className="h-4 w-4" />}
                                    {phase === 'restoring' ? 'Bərpa olunur…' : 'Bu tenantı bərpa et'}
                                </button>

                                {phase === 'restoring' && (
                                    <div className="mt-4">
                                        <div className="mb-1.5 flex justify-between text-xs font-medium text-slate-500">
                                            <span>Bərpa olunur (baza + şəkillər)…</span><span className="font-mono">{rpct}%</span>
                                        </div>
                                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                            <div className="h-full rounded-full bg-rose-500 transition-all" style={{ width: `${rpct}%` }} />
                                        </div>
                                    </div>
                                )}
                                <p className="mt-3 text-xs text-slate-400">
                                    Seçilmiş tenantın hazırkı məlumatı silinib backup ilə əvəzlənir. Digər tenantlara toxunulmur.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="mt-6">
                <RestoreCenter tenants={tenants} />
            </div>
        </SuperadminLayout>
    );
}
